import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { MapPin, Globe } from 'lucide-react';
import { Separator } from '../ui/separator';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_7d2805e4-0a10-4257-91cf-a5e52b7fdc5d/artifacts/2wr3xi3q_onemore-global-inc-logo.png';

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-[#001029] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="inline-block bg-white rounded-xl px-5 py-3.5 mb-6">
              <img
                src={LOGO_URL}
                alt="One More Global Inc."
                className="h-11 w-auto object-contain"
              />
            </div>
            <p className="text-base font-semibold text-white mb-1">
              One More Global Inc.
            </p>
            <p className="text-sm text-white/70 mb-5">
              {t('footer.tagline')}
            </p>
            <div className="flex items-start gap-2 text-sm text-white/40">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{t('footer.address')}</span>
            </div>
            <div className="mt-3 space-y-2">
              <a
                href="https://onemoreglobalinc.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors duration-200"
                data-testid="footer-link-website-primary"
              >
                <Globe className="w-4 h-4 shrink-0" />
                <span>onemoreglobalinc.com</span>
              </a>
              <a
                href="https://onemore.global"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors duration-200"
                data-testid="footer-link-website-secondary"
              >
                <Globe className="w-4 h-4 shrink-0" />
                <span>onemore.global</span>
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-5">
              {t('footer.company')}
            </h4>
            <ul className="space-y-3">
              {[
                { path: '/about', key: 'about' },
                { path: '/leadership', key: 'leadership' },
                { path: '/vision', key: 'vision' },
                { path: '/products', key: 'products' },
                { path: '/business-model', label: 'Business Model' },
                { path: '/operations', key: 'operations' },
                { path: '/structure', key: 'structure' },
                { path: '/compliance', key: 'compliance' },
                { path: '/brochures', label: t('brochures.label') },
              ].map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-sm text-white/45 hover:text-white transition-colors duration-200"
                  >
                    {item.label || t(`nav.${item.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-5">
              {t('footer.legal')}
            </h4>
            <ul className="space-y-3" data-testid="footer-legal-links">
              <li>
                <Link to="/terms" className="text-sm text-white/45 hover:text-white transition-colors duration-200" data-testid="footer-link-terms">
                  {t('legal.footer_terms')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-white/45 hover:text-white transition-colors duration-200" data-testid="footer-link-privacy">
                  {t('legal.footer_privacy')}
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="text-sm text-white/45 hover:text-white transition-colors duration-200" data-testid="footer-link-refund">
                  {t('legal.footer_refund')}
                </Link>
              </li>
              <li>
                <Link to="/shipping-policy" className="text-sm text-white/45 hover:text-white transition-colors duration-200" data-testid="footer-link-shipping">
                  {t('legal.footer_shipping')}
                </Link>
              </li>
              <li>
                <Link to="/income-disclosure" className="text-sm text-white/45 hover:text-white transition-colors duration-200" data-testid="footer-link-income">
                  {t('legal.footer_income')}
                </Link>
              </li>
              <li>
                <Link to="/fda-disclaimer" className="text-sm text-white/45 hover:text-white transition-colors duration-200" data-testid="footer-link-fda">
                  {t('legal.footer_fda')}
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-sm text-white/45 hover:text-white transition-colors duration-200" data-testid="footer-link-cookies">
                  {t('nav.cookies')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-5">
              {t('footer.connect')}
            </h4>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 text-sm font-semibold bg-[#B89B5E] text-[#001029] rounded-lg hover:bg-[#B89B5E]/90 transition-colors duration-200"
              data-testid="footer-contact-cta"
            >
              {t('nav.contact')}
            </Link>
          </div>
        </div>

        <Separator className="my-12 bg-white/6" />

        <p
          className="text-center text-xs text-white/40 leading-relaxed max-w-3xl mx-auto mb-6"
          data-testid="footer-fda-statement"
        >
          {t('legal.footer_statement')}
        </p>

        <div className="text-center text-xs text-white/35 tracking-wide">
          © {new Date().getFullYear()} One More Global Inc. All rights reserved.
        </div>
      </div>
    </footer>
  );
};
